import { useState } from 'react';
import { Empty, ErrorMsg, Loading, Modal, Section, Time, Unknown } from '../../components/ui';
import { api, errorText } from '../../lib/api';
import { fieldValue, PRICING_LABEL } from '../../lib/format';
import type { Evidence, Fact, SourceRow, StudioDetail } from '../../lib/types';

function sourceLink(src: SourceRow | undefined) {
  if (!src) return <span className="muted">source not recorded</span>;
  if (src.url)
    return (
      <a href={src.url} target="_blank" rel="noreferrer noopener">
        {src.title || src.url}
      </a>
    );
  return <span>{src.title ?? src.kind}</span>;
}

function FactList({ facts, evidence, empty }: { facts: Fact[]; evidence: Evidence | null; empty: string }) {
  const sources = new Map((evidence?.sources ?? []).map((s) => [s.id, s]));
  if (!facts.length) return <Empty>{empty}</Empty>;
  return (
    <ul className="facts">
      {facts.map((f) => (
        <li key={f.id} className="fact">
          <div className="fact-head">
            <span className="fact-value">{fieldValue(f.value)}</span>
            <span className="muted small">
              {' '}
              · {f.review_state.replace('_', ' ')} · {f.method.replace('_', ' ')}
              {f.confidence && ` · confidence ${f.confidence}`} · <Time iso={f.observed_at} />
            </span>
          </div>
          {f.excerpt && <blockquote className="excerpt">{f.excerpt}</blockquote>}
          <p className="small muted">Source: {sourceLink(f.source_id ? sources.get(f.source_id) : undefined)}</p>
        </li>
      ))}
    </ul>
  );
}

export function ServicesTab({ studio, evidence }: { studio: StudioDetail; evidence: Evidence | null }) {
  const facts = evidence?.facts ?? [];
  return (
    <div className="two-col">
      <Section title="Services">
        <p>{studio.services.length ? studio.services.join(', ') : <Unknown>none recorded</Unknown>}</p>
        <h3 className="sub">Evidence</h3>
        <FactList facts={facts.filter((f) => f.field === 'services')} evidence={evidence} empty="No evidence recorded for services." />
      </Section>
      <Section title="Client types">
        <p>{studio.client_types.length ? studio.client_types.join(', ') : <Unknown>none recorded</Unknown>}</p>
        <h3 className="sub">Evidence</h3>
        <FactList facts={facts.filter((f) => f.field === 'client_types')} evidence={evidence} empty="No evidence recorded for client types." />
      </Section>
      <p className="muted small span2">Individual projects (portfolio items) are not collected yet.</p>
    </div>
  );
}

export function PricingTab({ studio, evidence }: { studio: StudioDetail; evidence: Evidence | null }) {
  const facts = (evidence?.facts ?? []).filter((f) => f.field === 'pricing_evidence');
  return (
    <div className="narrow-col">
      <Section title="Pricing evidence">
        <p className="lede">{studio.pricing_evidence === 'not_checked' ? <Unknown>not checked yet</Unknown> : PRICING_LABEL[studio.pricing_evidence]}</p>
        <p className="small muted">
          “No evidence found” means the pages read contained no price; it does not mean the studio has no public prices.
        </p>
        <h3 className="sub">Price mentions and checks</h3>
        <FactList facts={facts} evidence={evidence} empty="No price mention recorded." />
      </Section>
      <p className="msg info">Supplier quotations (PDF, reviewed by a person) are on the Pricing page, owner only.</p>
    </div>
  );
}

export function CollectOutputs({ out }: { out: Record<string, unknown> | null }) {
  if (!out || !Object.keys(out).length) return <span className="muted">no outputs recorded</span>;
  const n = (k: string) => (typeof out[k] === 'number' ? (out[k] as number) : null);
  const changed = Array.isArray(out.changed_pages) ? (out.changed_pages as string[]) : [];
  if (n('pages_attempted') === null) return null;
  return (
    <div className="small">
      <p>
        <span className="num">{n('pages_fetched')}</span>/<span className="num">{n('pages_attempted')}</span> pages read · new{' '}
        <span className="num">{n('new_pages')}</span> · changed <span className="num">{changed.length}</span> · unchanged{' '}
        <span className="num">{n('unchanged_pages')}</span>
        {!!n('robots_disallowed') && (
          <>
            {' '}
            · robots.txt refused <span className="num">{n('robots_disallowed')}</span>
          </>
        )}
      </p>
      {changed.length > 0 && (
        <ul className="plain">
          {changed.map((u) => (
            <li key={u}>
              changed: <a href={u} target="_blank" rel="noreferrer noopener">{u}</a>
            </li>
          ))}
        </ul>
      )}
      {Array.isArray(out.facts_applied) && (out.facts_applied as string[]).length > 0 && <p>filled: {(out.facts_applied as string[]).join(', ')}</p>}
      {Array.isArray(out.facts_proposed) && (out.facts_proposed as string[]).length > 0 && <p>proposed: {(out.facts_proposed as string[]).join(', ')}</p>}
      {Array.isArray(out.errors) && (out.errors as string[]).length > 0 && <p className="err-text">errors: {(out.errors as string[]).join('; ')}</p>}
    </div>
  );
}

export function SourcesTab({ evidence, loading, error }: { evidence: Evidence | null; loading: boolean; error: string }) {
  const [view, setView] = useState<{ id: string; text: string | null; final_url: string | null; fetched_at: string | null } | null>(null);
  const [viewErr, setViewErr] = useState('');
  const [busy, setBusy] = useState<string | null>(null);

  async function open(id: string) {
    setBusy(id);
    setViewErr('');
    try {
      const r = await api<{ text: string | null; final_url: string | null; fetched_at: string | null }>(`/api/snapshots/${id}/text`);
      setView({ id, ...r });
    } catch (e) {
      setViewErr(errorText(e));
    } finally {
      setBusy(null);
    }
  }

  if (loading && !evidence) return <Loading what="Loading sources" />;
  if (!evidence) return <ErrorMsg>{error || 'Sources could not be loaded.'}</ErrorMsg>;
  return (
    <div>
      <ErrorMsg>{viewErr}</ErrorMsg>
      {evidence.sources.length === 0 ? (
        <Empty>No source recorded.</Empty>
      ) : (
        evidence.sources.map((s) => (
          <Section
            key={s.id}
            title={
              <>
                {s.title || s.url || s.kind} <span className="muted small">· {s.kind.replace('_', ' ')} · {s.visibility}</span>
              </>
            }
          >
            {s.url && (
              <p className="small">
                <a href={s.url} target="_blank" rel="noreferrer noopener">
                  {s.url}
                </a>
              </p>
            )}
            {s.snapshots.length === 0 ? (
              <p className="muted small">No snapshot: this source was recorded, not fetched (e.g. an import or something you entered).</p>
            ) : (
              <div className="table-wrap">
                <table className="table compact">
                  <thead>
                    <tr>
                      <th>Fetched</th>
                      <th>Outcome</th>
                      <th className="num">HTTP</th>
                      <th className="num">Bytes</th>
                      <th>Hash (sha256)</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {s.snapshots.map((sn) => (
                      <tr key={sn.id}>
                        <td>
                          <Time iso={sn.fetched_at} />
                        </td>
                        <td>
                          {sn.outcome}
                          {sn.error && <div className="small err-text">{sn.error}</div>}
                        </td>
                        <td className="num">{sn.http_status ?? '—'}</td>
                        <td className="num">{sn.bytes ?? '—'}</td>
                        <td className="mono small" title={sn.sha256 ?? ''}>
                          {sn.sha256 ? sn.sha256.slice(0, 12) : '—'}
                        </td>
                        <td>
                          {sn.outcome === 'fetched' && (
                            <button type="button" className="btn small" disabled={busy === sn.id} onClick={() => void open(sn.id)}>
                              View text
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Section>
        ))
      )}
      {view && (
        <Modal title="Normalised page text" onClose={() => setView(null)} wide>
          <p className="small muted">
            {view.final_url} · fetched <Time iso={view.fetched_at} />
          </p>
          <pre className="snapshot-text">{view.text ?? '(no text stored)'}</pre>
        </Modal>
      )}
    </div>
  );
}

export function NotesTab({ studio, onAdded }: { studio: StudioDetail; onAdded: (msg: string) => void }) {
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await api(`/api/studios/${studio.id}/notes`, { method: 'POST', body: { body } });
      setBody('');
      onAdded('Note added.');
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="narrow-col">
      <form onSubmit={submit} className="stack" aria-label="Add note">
        <label className="field">
          <span>New note</span>
          <textarea rows={3} value={body} onChange={(e) => setBody(e.target.value)} required />
        </label>
        <div>
          <button type="submit" className="btn primary small" disabled={busy || !body.trim()}>
            Add note
          </button>
        </div>
        <ErrorMsg>{error}</ErrorMsg>
      </form>
      {studio.notes.length === 0 ? (
        <Empty>No notes yet.</Empty>
      ) : (
        <ul className="notes">
          {studio.notes.map((n) => (
            <li key={n.id}>
              <p className="note-body">{n.body}</p>
              <p className="muted small">
                {n.author ?? 'unknown author'} · <Time iso={n.created_at} />
              </p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
